/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import SanitizedHtml from 'src/components/ui/SafeHtml/SanitizedHtml';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { localeDate, localeTime } from 'src/localization/locale';
import Button, { ButtonToneProps } from 'src/components/ui/Button/Button';
import Textarea from 'src/components/ui/Textarea/Textarea';
import { useTranslation } from 'react-i18next';
import useChat, { CaseBody, ChatItem, ChatKind, MentionCandidate, EntityCandidate } from 'src/services/chat';

import Icon from 'src/components/ui/Icon/Icon';
import AvatarImage from 'src/components/ui/Image/Image';
import Modal from 'src/components/layouts/Modal/Modal';
import { toast } from 'react-toastify';
import { Link } from 'react-router-dom';
import useUserProfile from 'src/services/profile';
import Select, { Option } from 'src/components/ui/Select/Select';
import ActionMenuButton from 'src/components/ui/ActionMenuButton/ActionMenuButton';
import Badge, { BadgeType } from '../../ui/Badge/Badge';
import styles from './Chat.module.scss';
import { Attachment } from './Attachment';

dayjs.extend(relativeTime);
dayjs.extend(utc);
dayjs.extend(timezone);

interface ChatProps {
  model: string;
  res_id: number;
  withCreateEditMessage?: boolean;
  condensed?: boolean;
  allowNote?: boolean;
  allowCase?: boolean;
  actionOptions?: Option[];
  onActionOption?: (value: string) => void;
}

const STORED_LINK_COLOR = /#1d4ed8/gi;
const STORED_LINK_SURFACE = /#dbeafe/gi;

const parseMessageBody = (body: string | null) =>
  (body ?? '').replace(STORED_LINK_COLOR, 'var(--chat-link)').replace(STORED_LINK_SURFACE, 'var(--chat-link-surface)');

const escapeRegExp = (value: string) => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const escapeHtml = (value: string) => value.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

const MENTION_PILL_STYLE = 'color:#1d4ed8;background:#dbeafe;border-radius:4px;padding:0 3px;font-weight:500';

const mentionPattern = (mentions: { name: string }[]) => {
  const names = mentions.map(m => m.name).sort((a, b) => b.length - a.length);
  return new RegExp(`@(${names.map(escapeRegExp).join('|')})`, 'g');
};

const buildMentionBody = (text: string, mentions: { id: number; name: string }[]) => {
  if (!mentions.length) return text;
  const idByName = new Map(mentions.map(m => [m.name, m.id]));
  return text.replace(
    mentionPattern(mentions),
    (_full, name) => `<span data-mention="${idByName.get(name)}" style="${MENTION_PILL_STYLE}">@${name}</span>`,
  );
};

type LinkedEntity = { id: number; model: string; name: string; deep_link: string };

const ENTITY_LINK_STYLE = 'color:#1d4ed8;text-decoration:underline;font-weight:500';

const ENTITY_TYPE_KEY: Record<string, string> = {
  'product.product': 'product',
  'sale.order': 'outbound',
  'purchase.order': 'inbound',
  'stock.picking': 'return',
  'project.task': 'case',
  'df.claim': 'claim',
  'res.country.group': 'carrier_group',
  'df.channel.connector': 'integration',
};

const toRelativePath = (url: string) => {
  try {
    const parsed = new URL(url);
    return `${parsed.pathname}${parsed.search}${parsed.hash}`;
  } catch {
    return url;
  }
};

const buildEntityBody = (text: string, links: LinkedEntity[]) => {
  if (!links.length) return text;
  const byName = new Map(links.map(l => [l.name, l]));
  const names = links.map(l => l.name).sort((a, b) => b.length - a.length);
  const pattern = new RegExp(`#(${names.map(escapeRegExp).join('|')})`, 'g');
  return text.replace(pattern, (_full, name) => {
    const link = byName.get(name);
    if (!link) return _full;
    return `<a href="${toRelativePath(link.deep_link)}" data-entity="${link.model},${link.id}" style="${ENTITY_LINK_STYLE}">#${name}</a>`;
  });
};

const renderComposerHtml = (text: string, mentions: { name: string }[], links: LinkedEntity[]) => {
  const tokens = [
    ...mentions.map(m => ({ token: `@${m.name}`, className: styles.composerPill })),
    ...links.map(l => ({ token: `#${l.name}`, className: styles.composerEntity })),
  ].sort((a, b) => b.token.length - a.token.length);
  if (!tokens.length) return escapeHtml(text);
  const classByToken = new Map(tokens.map(tk => [tk.token, tk.className]));
  const pattern = new RegExp(`(${tokens.map(tk => escapeRegExp(tk.token)).join('|')})`, 'g');
  let lastIndex = 0;
  let out = '';
  let match = pattern.exec(text);
  while (match) {
    out += escapeHtml(text.slice(lastIndex, match.index));
    const full = match[0];
    out += `<span class="${classByToken.get(full)}">${escapeHtml(full)}</span>`;
    lastIndex = match.index + full.length;
    match = pattern.exec(text);
  }
  out += escapeHtml(text.slice(lastIndex));
  return out;
};

const HIGHLIGHTED_CATEGORIES = ['status_change', 'resolution', 'document_request'];

const PERSON_COLOR_VARS = [
  '--status-neutral-2-full',
  '--status-neutral-4-full',
  '--status-success-full',
  '--status-warning-full',
  '--status-neutral-5-full',
  '--status-neutral-3-full',
];

const personColor = (name: string | null) => {
  const seed = Array.from(name || '').reduce((sum, ch) => sum + ch.charCodeAt(0), 0);
  return `var(${PERSON_COLOR_VARS[seed % PERSON_COLOR_VARS.length]})`;
};

const initialsOf = (name: string | null) =>
  (name || '')
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map(part => part.charAt(0).toUpperCase())
    .join('');

const kindOf = (message: ChatItem): ChatKind => {
  if (message.kind) return message.kind;
  if (message.is_note) return 'note';
  return message.message_type === 'comment' ? 'message' : 'system';
};

const isProtocolKind = (kind: ChatKind) => kind === 'status_change' || kind === 'system' || kind === 'created';

const COLLAPSE_STATUS_RUN_AFTER = 3;
const COLLAPSED_STATUS_VISIBLE = 2;

type TimelineEntry =
  | { type: 'date'; key: string; label: string }
  | { type: 'message'; key: string; message: ChatItem }
  | { type: 'toggle'; key: string; groupKey: string; hidden: number; expanded: boolean };

const Chat: React.FC<ChatProps> = ({
  model,
  res_id,
  withCreateEditMessage = true,
  condensed = false,
  allowNote = false,
  allowCase = true,
  actionOptions,
  onActionOption,
}) => {
  const { t } = useTranslation();
  const { profile } = useUserProfile();
  const {
    messages,
    createMessage,
    deleteMessage,
    updateMessage,
    error,
    fetchMoreMessages,
    allMessagesFetched,
    loading,
    createCaseFromMessage,
    searchMentionable,
    searchLinkable,
  } = useChat(model, res_id);
  const [newMessage, setNewMessage] = useState('');
  const [mentioned, setMentioned] = useState<{ id: number; name: string }[]>([]);
  const [mentionResults, setMentionResults] = useState<MentionCandidate[]>([]);
  const [mentionOpen, setMentionOpen] = useState(false);
  const [linked, setLinked] = useState<{ id: number; model: string; name: string; deep_link: string }[]>([]);
  const [entityResults, setEntityResults] = useState<EntityCandidate[]>([]);
  const [entityOpen, setEntityOpen] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const mentionTokenRef = useRef<{ start: number; end: number }>({ start: 0, end: 0 });
  const mentionDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const entityTokenRef = useRef<{ start: number; end: number }>({ start: 0, end: 0 });
  const entityDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [messageType, setMessageType] = useState(allowCase ? 'is_case' : 'is_message');
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [attachments, setAttachments] = useState<{ name: string; file: string; mimetype: string }[]>([]);
  const [editingMessageId, setEditingMessageId] = useState<number | null>(null);
  const [deleteMessageId, setDeleteMessageId] = useState<number | null>(null);
  // Bumped to remount (and thus reset) the "Markieren als" dropdown after an
  // action option is picked, so it falls back to the default entry.
  const [markAsResetKey, setMarkAsResetKey] = useState(0);
  const [isModalOpen, setModalOpen] = useState<boolean>(false);
  const [editingMessageText, setEditingMessageText] = useState('');
  const [expandedStatusGroups, setExpandedStatusGroups] = useState<Set<string>>(new Set());

  const dateLabel = useCallback(
    (value: string) => {
      const day = dayjs(value);
      if (day.isSame(dayjs(), 'day')) return t('chat.today');
      if (day.isSame(dayjs().subtract(1, 'day'), 'day')) return t('chat.yesterday');
      return localeDate(day.toDate());
    },
    [t],
  );

  const timeline = useMemo<TimelineEntry[]>(() => {
    const entries: TimelineEntry[] = [];
    let lastDay = '';
    let index = 0;
    while (index < messages.length) {
      const message = messages[index];
      const day = dayjs(message.timestamp).format('YYYY-MM-DD');
      if (day !== lastDay) {
        entries.push({ type: 'date', key: `date-${day}`, label: dateLabel(message.timestamp) });
        lastDay = day;
      }
      if (kindOf(message) !== 'status_change') {
        entries.push({ type: 'message', key: `m-${message.id}`, message });
        index += 1;
      } else {
        const run: ChatItem[] = [];
        while (
          index < messages.length &&
          kindOf(messages[index]) === 'status_change' &&
          dayjs(messages[index].timestamp).format('YYYY-MM-DD') === day
        ) {
          run.push(messages[index]);
          index += 1;
        }
        const groupKey = `status-${run[0].id}`;
        const expanded = expandedStatusGroups.has(groupKey);
        const collapsible = run.length > COLLAPSE_STATUS_RUN_AFTER;
        const visible = collapsible && !expanded ? run.slice(0, COLLAPSED_STATUS_VISIBLE) : run;
        visible.forEach(item => entries.push({ type: 'message', key: `m-${item.id}`, message: item }));
        if (collapsible) {
          entries.push({
            type: 'toggle',
            key: `toggle-${groupKey}`,
            groupKey,
            hidden: run.length - COLLAPSED_STATUS_VISIBLE,
            expanded,
          });
        }
      }
    }
    return entries;
  }, [messages, expandedStatusGroups, dateLabel]);

  const toggleStatusGroup = (groupKey: string) =>
    setExpandedStatusGroups(previous => {
      const next = new Set(previous);
      if (next.has(groupKey)) next.delete(groupKey);
      else next.add(groupKey);
      return next;
    });

  const messagesScrollContainerRef = useRef<HTMLDivElement | null>(null);
  const isLoadingMoreMessages = useRef(false); // Use ref instead of state for faster updates

  const fetchMoreMessagesOnScroll = useCallback(() => {
    if (!messagesScrollContainerRef.current || allMessagesFetched || isLoadingMoreMessages.current) return;

    const { scrollTop, scrollHeight, clientHeight } = messagesScrollContainerRef.current;
    const scrollThreshold = 40;

    if (messages.length && scrollTop + clientHeight >= scrollHeight - scrollThreshold) {
      isLoadingMoreMessages.current = true;
      fetchMoreMessages();
    }
  }, [fetchMoreMessages, allMessagesFetched, messages.length]);

  const messageTypeOptions = useMemo(
    () =>
      [
        { label: t('chat.is_message'), value: 'is_message' },
        ...(allowCase ? [{ label: t('chat.is_case'), value: 'is_case' }] : []),
        ...(allowNote ? [{ label: t('chat.is_note'), value: 'is_note' }] : []),
      ] as Option[],
    [t, allowCase, allowNote],
  );

  const markAsOptions = useMemo(
    () => [...messageTypeOptions, ...(actionOptions ?? [])],
    [messageTypeOptions, actionOptions],
  );

  const handleMarkAsChange = (option: Option) => {
    if (actionOptions?.some(o => o.value === option.value)) {
      onActionOption?.(option.value);
      setMarkAsResetKey(k => k + 1); // reset dropdown back to the default entry
      return;
    }
    setMessageType(option.value);
  };

  useEffect(() => {
    isLoadingMoreMessages.current = false;
  }, [messages]);

  useEffect(() => {
    const scrollContainer = messagesScrollContainerRef.current;
    if (scrollContainer) {
      scrollContainer.addEventListener('scroll', fetchMoreMessagesOnScroll);
    }

    return () => {
      if (scrollContainer) {
        scrollContainer.removeEventListener('scroll', fetchMoreMessagesOnScroll);
      }
    };
  }, [fetchMoreMessagesOnScroll]);

  const detectMention = (value: string, caret: number) => {
    const match = value.slice(0, caret).match(/(?:^|\s)@([^\s@]*)$/);
    if (!match) {
      setMentionOpen(false);
      return;
    }
    const query = match[1];
    mentionTokenRef.current = { start: caret - query.length - 1, end: caret };
    if (mentionDebounceRef.current) clearTimeout(mentionDebounceRef.current);
    mentionDebounceRef.current = setTimeout(async () => {
      const results = await searchMentionable(query);
      setMentionResults(results);
      setMentionOpen(results.length > 0);
    }, 150);
  };

  const detectEntity = (value: string, caret: number) => {
    const match = value.slice(0, caret).match(/(?:^|\s)#([^\s#]*)$/);
    if (!match) {
      setEntityOpen(false);
      return;
    }
    const query = match[1];
    entityTokenRef.current = { start: caret - query.length - 1, end: caret };
    if (entityDebounceRef.current) clearTimeout(entityDebounceRef.current);
    entityDebounceRef.current = setTimeout(async () => {
      const results = await searchLinkable(query);
      setEntityResults(results);
      setEntityOpen(results.length > 0);
    }, 150);
  };

  const handleMessageChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const { value, selectionStart } = e.target;
    setNewMessage(value);
    const caret = selectionStart ?? value.length;
    detectMention(value, caret);
    detectEntity(value, caret);
  };

  const selectMention = (candidate: MentionCandidate) => {
    const { start, end } = mentionTokenRef.current;
    const before = newMessage.slice(0, start);
    const after = newMessage.slice(end);
    const inserted = `@${candidate.name} `;
    setNewMessage(`${before}${inserted}${after}`);
    setMentioned(prev =>
      prev.some(m => m.id === candidate.id) ? prev : [...prev, { id: candidate.id, name: candidate.name }],
    );
    setMentionOpen(false);
    const caret = before.length + inserted.length;
    requestAnimationFrame(() => {
      textareaRef.current?.focus();
      textareaRef.current?.setSelectionRange(caret, caret);
    });
  };

  const selectEntity = (candidate: EntityCandidate) => {
    const { start, end } = entityTokenRef.current;
    const before = newMessage.slice(0, start);
    const after = newMessage.slice(end);
    const inserted = `#${candidate.name} `;
    setNewMessage(`${before}${inserted}${after}`);
    setLinked(prev =>
      prev.some(l => l.model === candidate.model && l.id === candidate.id)
        ? prev
        : [...prev, { id: candidate.id, model: candidate.model, name: candidate.name, deep_link: candidate.deep_link }],
    );
    setEntityOpen(false);
    const caret = before.length + inserted.length;
    requestAnimationFrame(() => {
      textareaRef.current?.focus();
      textareaRef.current?.setSelectionRange(caret, caret);
    });
  };

  const handleSendMessage = async () => {
    if (!newMessage.trim() && !attachments.length) return; // Prevent sending empty messages without attachment

    const createCase = messageType === 'is_case';
    const isNote = messageType === 'is_note';
    const activeMentions = mentioned.filter(m => newMessage.includes(`@${m.name}`));
    const mentionedPartnerIds = activeMentions.map(m => m.id);
    const activeLinks = linked.filter(l => newMessage.includes(`#${l.name}`));
    const body = buildEntityBody(buildMentionBody(newMessage, activeMentions), activeLinks);

    // Pass message and attachment (if exists) to createMessage
    const success = await createMessage(body, attachments, createCase, isNote, mentionedPartnerIds);

    if (success) {
      setNewMessage('');
      setMentioned([]);
      setMentionOpen(false);
      setLinked([]);
      setEntityOpen(false);
      setSelectedFiles([]);
      setAttachments([]);
      if (messagesScrollContainerRef.current) {
        messagesScrollContainerRef.current.scrollTop = 0;
      }
    }
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const allSelectedFiles = [...selectedFiles, file];
      setSelectedFiles(allSelectedFiles);
      const reader = new FileReader();
      reader.onloadend = () => {
        // Convert file to Base64
        const base64String = reader.result as string;
        const base64StringNoPrefix = base64String.replace(/^data:.*;base64,/, '');
        const newAttachments = [
          ...attachments,
          {
            file: base64StringNoPrefix,
            mimetype: file.type,
            name: file.name,
          },
        ];
        setAttachments(newAttachments);
      };
      reader.readAsDataURL(file);
      // Reset the file input to allow re-selection of the same file
      event.target.value = '';
    }
  };

  const handleRemoveSelectedFile = (index: number) => {
    const newFiles = selectedFiles.filter((selectedFile, selectedFileIndex) => index !== selectedFileIndex);
    const newAttachments = attachments.filter((attachment, attachmentIndex) => index !== attachmentIndex);
    setSelectedFiles(newFiles);
    setAttachments(newAttachments);
  };

  useEffect(() => {
    toast.error(error);
  }, [error]);

  const removeHtmlFromText = (text: string) => text.replace(/<\/?[^>]+(>|$)/g, '');

  const onUpdateMessage = async (id: number, message: string) => {
    const success = await updateMessage(id, message);
    if (success) {
      setEditingMessageId(null);
      setEditingMessageText('');
    }
  };

  const onDeleteMessage = async () => {
    if (deleteMessageId) {
      await deleteMessage(deleteMessageId);
    }
    setDeleteMessageId(null);
    setModalOpen(false);
  };

  return (
    <>
      <div className={`${styles.chatContainer} ${condensed ? styles.condensed : ''}`}>
        {withCreateEditMessage && (
          <div className={styles.inputContainer}>
            <div className="typo-text-16--bold text-full">{t('chat.chat')}</div>
            <div className="w-full">
              <div className="grid grid-cols-[max-content_1fr] gap-y-12 gap-x-24">
                <div className={`${styles.inputField} relative`}>
                  <SanitizedHtml
                    aria-hidden
                    className="pointer-events-none absolute inset-0 overflow-hidden whitespace-pre-wrap break-words rounded-base surface-elevated px-9 py-12 pr-30 typo-text-14 text-full"
                    style={{ border: '1px solid transparent' }}
                    html={renderComposerHtml(newMessage, mentioned, linked)}
                  />
                  <Textarea
                    ref={textareaRef}
                    onChange={handleMessageChange}
                    onBlur={() => setTimeout(() => setMentionOpen(false), 150)}
                    value={newMessage}
                    placeholder={t('chat.type_message_placeholder')}
                    className="relative w-full"
                    style={{ background: 'transparent', color: 'transparent', caretColor: 'var(--text-full)' }}
                    addPaddingForIconRight
                  />
                  {mentionOpen && (
                    <div
                      className="absolute left-0 right-0 top-full z-30 mt-4 max-h-[240px] overflow-y-auto rounded-[10px] surface-elevated border border-subtle"
                      style={{ boxShadow: 'none' }}>
                      {mentionResults.map(candidate => (
                        <div
                          key={candidate.id}
                          role="none"
                          onMouseDown={e => e.preventDefault()}
                          onClick={() => selectMention(candidate)}
                          className="flex items-center gap-[12px] px-[14px] py-[10px] cursor-pointer hover:surface-elevated-more">
                          <div
                            className="flex items-center justify-center w-[32px] h-[32px] rounded-full typo-text-14--bold shrink-0"
                            style={{ background: 'var(--primary-tint)', color: 'var(--primary)' }}>
                            {candidate.name.charAt(0).toUpperCase()}
                          </div>
                          <div className="flex flex-col min-w-0">
                            <span className="typo-text-14--medium truncate">{candidate.name}</span>
                            <span className="typo-text-12 text-faded truncate">{candidate.email}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  {entityOpen && (
                    <div
                      className="absolute left-0 right-0 top-full z-30 mt-4 max-h-[240px] overflow-y-auto rounded-[10px] surface-elevated border border-subtle"
                      style={{ boxShadow: 'none' }}>
                      {entityResults.map(candidate => (
                        <div
                          key={`${candidate.model}-${candidate.id}`}
                          role="none"
                          onMouseDown={e => e.preventDefault()}
                          onClick={() => selectEntity(candidate)}
                          className="flex items-center gap-[12px] px-[14px] py-[10px] cursor-pointer hover:surface-elevated-more">
                          <div
                            className="flex items-center justify-center px-[8px] h-[22px] rounded-[6px] typo-text-12--bold shrink-0 whitespace-nowrap"
                            style={{ background: 'var(--primary-tint)', color: 'var(--primary)' }}>
                            {ENTITY_TYPE_KEY[candidate.model]
                              ? t(`chat.entity_type.${ENTITY_TYPE_KEY[candidate.model]}`)
                              : candidate.type_label}
                          </div>
                          <div className="flex flex-col min-w-0">
                            <span className="typo-text-14--medium truncate">{candidate.name}</span>
                            <span className="typo-text-12 text-faded truncate">{candidate.reference}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <span className="typo-text-12--bold whitespace-nowrap text-faded mt-10">{t('chat.mark_as')}</span>
                <Select
                  key={markAsResetKey}
                  options={markAsOptions}
                  onChange={handleMarkAsChange}
                  defaultValue={messageTypeOptions.find(o => o.value === 'is_case') ?? messageTypeOptions[0]}
                  disabled={!withCreateEditMessage}
                />
                <span className="typo-text-12--bold whitespace-nowrap text-faded mt-10">
                  {t('chat.add_attachment')}
                </span>
                <div className="flex flex-wrap items-center gap-16 max-w-full">
                  <Button
                    title={t('general.select_files')}
                    tone={ButtonToneProps.SECONDARY}
                    onClick={() => document.getElementById('file-input')?.click()}
                    disabled={!withCreateEditMessage}
                  />
                  {selectedFiles.length > 0 ? (
                    selectedFiles.map((selectedFile, index) => (
                      <div className="flex items-center">
                        <Icon name="attachFile" width={20} height={20} className="text-faded cursor-pointer" />
                        <span className="mx-8">{selectedFile.name}</span>
                        <div role="none" onClick={() => handleRemoveSelectedFile(index)}>
                          <Icon name="close" width={20} height={20} className="cursor-pointer text-faded" />
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="flex items-center">
                      <Icon name="attachFile" width={20} height={20} className="text-faded cursor-pointer" />
                      <span className="mx-8 typo-text-12--bold text-faded">{t('general.no_files_selected')}</span>
                    </div>
                  )}
                </div>
              </div>
              <div
                role="none"
                className={styles.attachment}
                onClick={() => document.getElementById('file-input')?.click()}>
                <input type="file" id="file-input" style={{ display: 'none' }} onChange={handleFileSelect} />
              </div>
            </div>
            <Button
              className="mt-6 self-end"
              onClick={handleSendMessage}
              disabled={!newMessage && !attachments.length} // Enable send button if there is text or an attachment
              tone={ButtonToneProps.PRIMARY_BLUE}
              title={t('chat.send')}
            />
          </div>
        )}

        <div className="flex flex-col gap-y-20">
          <p className="typo-text-14--bold text-faded">{t('chat.conversation-log')}</p>
          <div className={styles.messages} ref={messagesScrollContainerRef}>
            <div className={styles.timeline}>
              {timeline.map(entry => {
                if (entry.type === 'date') {
                  return (
                    <div key={entry.key} className={styles.dateDivider}>
                      <span className={styles.dateDividerLine} />
                      <span className={styles.dateDividerLabel}>{entry.label}</span>
                      <span className={styles.dateDividerLine} />
                    </div>
                  );
                }
                if (entry.type === 'toggle') {
                  return (
                    <button
                      key={entry.key}
                      type="button"
                      className={styles.collapseToggle}
                      onClick={() => toggleStatusGroup(entry.groupKey)}>
                      <Icon
                        name={entry.expanded ? 'accordion-arrow-up' : 'accordion-arrow-down'}
                        width={16}
                        height={16}
                      />
                      {entry.expanded ? t('chat.show_less') : t('chat.more_changes', { count: entry.hidden })}
                    </button>
                  );
                }

                const { message } = entry;
                const kind = kindOf(message);
                const name = message.user_from || '';
                const color = personColor(name);
                const time = localeTime(dayjs(message.timestamp).toDate());

                if (kind === 'status_change') {
                  return (
                    <div key={entry.key} className={styles.sysRow}>
                      <span className={styles.sysIcon}>
                        <Icon name="arrowRight" width={14} height={14} />
                      </span>
                      <div className={styles.sysText}>
                        {message.tracking?.length ? (
                          <div className={styles.sysChanges}>
                            {message.tracking.map(change => (
                              <div key={`${message.id}-${change.field}`} className={styles.sysChange}>
                                <span>
                                  <strong className={styles.personName} style={{ color }}>
                                    {name}
                                  </strong>{' '}
                                  {t('chat.changed_field', { field: change.field })}
                                </span>
                                <Badge label={change.old || '—'} type={BadgeType.Neutral} expanded />
                                <Icon name="arrowRight" width={14} height={14} className="text-faded" />
                                <Badge label={change.new || '—'} type={BadgeType.Neutral4} expanded />
                              </div>
                            ))}
                          </div>
                        ) : (
                          <span>
                            <strong style={{ color }}>{name}</strong>{' '}
                            <SanitizedHtml as="span" html={parseMessageBody(message.body?.message)} />
                          </span>
                        )}
                      </div>
                      <span className={styles.time}>{time}</span>
                    </div>
                  );
                }

                if (isProtocolKind(kind)) {
                  return (
                    <div key={entry.key} className={styles.sysRow}>
                      <span className={styles.sysIcon}>
                        {message.avatar ? (
                          <AvatarImage
                            imageUrl={message.avatar}
                            alt={`avatar_${name}`}
                            fallbackComponent={<span className={styles.sysInitials}>{initialsOf(name)}</span>}
                          />
                        ) : (
                          <Icon name={kind === 'created' ? 'plus' : 'infoOutlined'} width={14} height={14} />
                        )}
                      </span>
                      <div className={styles.sysText}>
                        <strong className={styles.personName} style={{ color }}>
                          {name}
                        </strong>
                        {' · '}
                        <SanitizedHtml as="span" html={parseMessageBody(message.body?.message)} />
                      </div>
                      <span className={styles.time}>{time}</span>
                    </div>
                  );
                }

                const isOwn = message.is_own ?? ['Client Portal Registration User', profile.name].includes(name);
                const messageAttachments = message.body.attachments || [];
                const highlighted = HIGHLIGHTED_CATEGORIES.includes(message.category || '');
                const canEdit = isOwn && withCreateEditMessage;
                const showToolbar = canEdit && (message.is_editable || message.is_deletable || !message.is_case);

                return (
                  <div key={entry.key} className={`${styles.msgRow} ${isOwn ? styles.msgRowOwn : ''}`}>
                    <div className={styles.avatar} style={{ background: color }}>
                      <AvatarImage
                        imageUrl={message.avatar}
                        alt={`avatar_${name}`}
                        fallbackComponent={<span className={styles.avatarInitials}>{initialsOf(name)}</span>}
                      />
                    </div>
                    <div className={`${styles.msgCol} ${isOwn ? styles.msgColOwn : ''}`}>
                      <div className={styles.msgHead}>
                        <span className={styles.personName} style={{ color }}>
                          {name}
                        </span>
                        {kind === 'note' && (
                          <Badge
                            type={BadgeType.Neutral5}
                            label={t('chat.note')}
                            expanded
                            showBg={false}
                            dotFilled
                            showDot
                          />
                        )}
                        <span className={styles.time}>{time}</span>
                      </div>
                      <div className={styles.bubbleWrap}>
                        <div
                          className={`${styles.bubble} ${isOwn ? styles.bubbleOwn : ''} ${
                            kind === 'note' ? styles.bubbleNote : ''
                          } ${highlighted ? 'status-warning-surface status-warning-full' : ''}`}>
                          {message.is_case && (
                            <Link
                              className="typo-text-14--bold text-full underline"
                              to={`/cases/${(message.body as CaseBody)?.case_id}`}>
                              {`${t('chat.case')} #${(message.body as CaseBody)?.case_id}: ${t('chat.status')} ${(message.body as CaseBody)?.status}`}
                            </Link>
                          )}
                          {editingMessageId === message.id ? (
                            <div>
                              <Textarea
                                value={editingMessageText}
                                onChange={e => setEditingMessageText(e.target.value)}
                                className={styles.inputField}
                                placeholder={t('chat.type_message_placeholder')}
                              />
                              <div className={styles.editControls}>
                                <Button
                                  onClick={() => {
                                    onUpdateMessage(message.id, editingMessageText);
                                  }}
                                  tone={ButtonToneProps.PRIMARY_BLUE}
                                  title={t('chat.save')}
                                />
                                <Button
                                  onClick={() => {
                                    setEditingMessageId(null);
                                    setEditingMessageText('');
                                  }}
                                  tone={ButtonToneProps.SECONDARY}
                                  title={t('chat.cancel')}
                                />
                              </div>
                            </div>
                          ) : (
                            <SanitizedHtml
                              className={styles.messageText}
                              html={parseMessageBody(message.body?.message)}
                            />
                          )}
                          {messageAttachments.length > 0 &&
                            messageAttachments.map(messageAttachment => (
                              <div key={messageAttachment.id} className="self-start">
                                <Attachment attachment={messageAttachment} t={t} />
                              </div>
                            ))}
                        </div>
                        {showToolbar && editingMessageId !== message.id && (
                          <div className={styles.toolbar}>
                            {message.is_editable && (
                              <button
                                type="button"
                                aria-label={t('chat.edit')}
                                className={styles.toolbarButton}
                                onClick={() => {
                                  setEditingMessageId(message.id);
                                  setEditingMessageText(removeHtmlFromText(message.body.message || ''));
                                }}>
                                <Icon name="edit" width={16} height={16} />
                              </button>
                            )}
                            {message.is_deletable && (
                              <button
                                type="button"
                                aria-label={t('general.delete')}
                                className={styles.toolbarButton}
                                onClick={() => {
                                  setDeleteMessageId(message.id);
                                  setModalOpen(true);
                                }}>
                                <Icon name="delete" width={16} height={16} />
                              </button>
                            )}
                            {!message.is_case && (
                              <ActionMenuButton
                                menuItems={[
                                  {
                                    label: t('chat.create_case'),
                                    onClick: () => createCaseFromMessage(message.id),
                                  },
                                ]}
                              />
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
      <Modal
        hasBackdrop
        isOpen={isModalOpen}
        onClose={() => {
          setDeleteMessageId(null);
          setModalOpen(false);
        }}>
        <div>
          <div className="border-subtle-bottom p-32 ">
            <h4 className="mb-12 typo-title-24">{t('chat.delete_message_title')}</h4>
            <p className="typo-text-16 text-faded">{t('chat.delete_message_text')}</p>
          </div>
          <div className="border-subtle-bottom py-16 px-24 flex justify-end">
            <Button
              className="mr-12"
              onClick={() => {
                setDeleteMessageId(null);
                setModalOpen(false);
              }}
              tone={ButtonToneProps.SECONDARY}
              title={t('general.cancel')}
            />
            <Button onClick={onDeleteMessage} tone={ButtonToneProps.PRIMARY_BLUE} title={t('general.delete')} />
          </div>
        </div>
      </Modal>
    </>
  );
};

export default Chat;
